import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CompetitionMetadata, listCompetitions } from '../firebase/competitionService';

const STATUS_LABEL: Record<string, string> = {
  planning: 'Planning',
  live:     'Live',
  finished: 'Finished',
};
const STATUS_COLOR: Record<string, string> = {
  planning: 'bg-gray-100 text-gray-600',
  live:     'bg-green-100 text-green-700',
  finished: 'bg-blue-100 text-blue-700',
};

const CompetitionsListPage: React.FC = () => {
  const [competitions, setCompetitions] = useState<CompetitionMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listCompetitions()
      .then(list => { setCompetitions(list); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-dark to-secondary text-white py-10 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-extrabold tracking-tight">Competitions</h1>
          <p className="text-blue-200 mt-1 text-lg">All registered competitions</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-end mb-6">
          <Link to="/competition/new"
            className="inline-flex items-center gap-2 px-5 py-2 bg-secondary text-white rounded-lg font-semibold hover:bg-blue-800 transition-colors">
            + New competition
          </Link>
        </div>

        {loading && (
          <div className="text-center text-gray-400 py-16 text-lg">Loading…</div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
        )}
        {!loading && !error && competitions.length === 0 && (
          <div className="bg-white rounded-lg shadow p-10 text-center text-gray-400">
            <p className="text-lg">No competitions yet.</p>
            <Link to="/competition/new" className="mt-4 inline-block text-secondary underline">Create your first competition</Link>
          </div>
        )}
        {!loading && competitions.length > 0 && (
          <div className="flex flex-col gap-4">
            {competitions.map(c => (
              <Link key={c.id} to={`/competition/${c.id}`}
                className="bg-white rounded-lg shadow hover:shadow-md transition-shadow px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 no-underline">
                <div>
                  <div className="text-lg font-bold text-dark">{c.name}</div>
                  <div className="text-sm text-gray-500 mt-0.5">
                    {c.date && <span className="mr-3">{c.date}</span>}
                    {c.location && <span>{c.location}</span>}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {c.disciplines.map(d => (
                      <span key={d} className="text-xs font-semibold px-2 py-0.5 rounded bg-secondary/10 text-secondary capitalize">{d}</span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLOR[c.status] ?? 'bg-gray-100 text-gray-600'}`}>
                    {STATUS_LABEL[c.status] ?? c.status}
                  </span>
                  <span className="text-secondary font-semibold text-sm">View →</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompetitionsListPage;
